import { useMemo } from 'react'
import { Chip, KanbanBoard, type KanbanItem, type KanbanStage } from '@/components/ui'
import { usePreferences } from '@/shell/stores/preferences'
import type { Packet, Stage } from '../api/types'
import { BOARD_STAGES, STAGE_SHORT_LABELS, STAGE_VARIANT, transitionAllowed } from '../utils/stage-meta'

const MODULE_ID = 'export-docs'

interface PacketKanbanItem extends KanbanItem {
  packet: Packet
}

export interface PacketsKanbanProps {
  items: Packet[]
  selectedId?: string
  onSelect: (packet: Packet) => void
  onTransition: (packet: Packet, target: Stage) => void
}

function kanbanAccent(
  v: 'neutral' | 'success' | 'warning' | 'danger' | 'info',
): 'neutral' | 'success' | 'warning' | 'danger' {
  // KanbanStage's accent doesn't have an `info` slot — fold info → neutral.
  if (v === 'info') return 'neutral'
  return v
}

const STAGE_BY_ID: Record<Stage, KanbanStage> = BOARD_STAGES.reduce(
  (acc, s) => {
    acc[s] = { id: s, label: STAGE_SHORT_LABELS[s], accent: kanbanAccent(STAGE_VARIANT[s]) }
    return acc
  },
  {} as Record<Stage, KanbanStage>,
)

function composeStageOrder(userOrder: ReadonlyArray<string>): KanbanStage[] {
  const seen = new Set<string>()
  const out: KanbanStage[] = []
  for (const id of userOrder) {
    if (id in STAGE_BY_ID && !seen.has(id)) {
      out.push(STAGE_BY_ID[id as Stage])
      seen.add(id)
    }
  }
  for (const s of BOARD_STAGES) {
    if (!seen.has(s)) {
      out.push(STAGE_BY_ID[s])
      seen.add(s)
    }
  }
  return out
}

/**
 * Kanban view over the export-docs pipeline. Drag-gating uses
 * `transitionAllowed()` from `utils/stage-meta.ts`, which mirrors the
 * backend's narrow, linear `ALLOWED_TRANSITIONS` table exactly — this is
 * NOT a free-drag board. A drop on a column the packet can't legally
 * move to is silently rejected by `KanbanBoard` itself.
 */
export function PacketsKanban({ items, selectedId, onSelect, onTransition }: PacketsKanbanProps) {
  const userOrder = usePreferences((s) => s.kanbanStageOrder[MODULE_ID]) ?? []
  const setKanbanStageOrder = usePreferences((s) => s.setKanbanStageOrder)

  const stages = useMemo(() => composeStageOrder(userOrder), [userOrder])

  const kanbanItems: PacketKanbanItem[] = useMemo(
    () =>
      items.map((packet) => ({
        id: String(packet.id),
        stageId: packet.stage,
        packet,
      })),
    [items],
  )

  return (
    <KanbanBoard<PacketKanbanItem>
      stages={stages}
      items={kanbanItems}
      selectedId={selectedId}
      allowedTransitions={(from, to) => transitionAllowed(from as Stage, to as Stage)}
      onAdvance={(item, _from, to) => onTransition(item.packet, to as Stage)}
      onSelect={(item) => onSelect(item.packet)}
      onStageReorder={(next) => setKanbanStageOrder(MODULE_ID, next)}
      renderCard={(item) => <PacketKanbanCard packet={item.packet} />}
      scrollRestorationKey="export-docs"
    />
  )
}

function PacketKanbanCard({ packet }: { packet: Packet }) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="text-role-data-primary truncate" title={packet.picking_name ?? ''}>
        {packet.picking_name ?? <em className="text-tertiary">unnamed shipment</em>}
      </div>
      <div className="text-role-data-secondary truncate" title={packet.ordering_party_name ?? ''}>
        {packet.ordering_party_name ?? <em className="text-tertiary">unknown ordering party</em>}
      </div>
      <div className="text-role-metadata truncate">{packet.lane_name ?? '—'}</div>
      <div className="flex items-center gap-1.5">
        {packet.is_hazardous ? (
          <Chip size="xs" variant="warning">
            Hazmat
          </Chip>
        ) : null}
        {packet.coo_required ? (
          <Chip size="xs" variant="info">
            CoO
          </Chip>
        ) : null}
      </div>
      {packet.error_message ? (
        <div className="line-clamp-2 text-role-metadata text-danger" title={packet.error_message}>
          {packet.error_message}
        </div>
      ) : null}
    </div>
  )
}
